/**
 * Browser-side helper for the /api/chat Worker proxy.
 *
 * Contract: design/06-infra-brief.md.
 * The game code never knows about Anthropic or system prompts — it picks
 * a character short name, supplies a trigger + game_state + history, and
 * gets back a line of dialogue.
 *
 *   import { chat, type GameState } from "./lib/chat";
 *
 *   const reply = await chat({
 *     character: "flere",
 *     trigger: "wave_start",
 *     pilot_utterance: null,
 *     game_state: { wave: 1, score: 0, ...  },
 *     history: [],
 *   });
 *   console.log(reply.text);          // "Morning, Gurgeh."
 *   if (reply.error) { ...fallback... }
 */

export type CharacterId =
  | "flere"
  | "qt"
  | "special_circumstances"
  | "market_report"
  | "lavender"
  | "retro_hour";

export type Trigger =
  | "pilot_submit"
  | "wave_start"
  | "wave_end"
  | "event"
  | "idle"
  | "ambient";

export type HistoryTurn = {
  role: "user" | "assistant";
  content: string;
};

/**
 * Open schema — the game owns these fields. Worker passes through as JSON.
 * Keep it flat-ish; Flere speaks to whatever you put in here.
 */
export type GameState = {
  wave?: number;
  score?: number;
  surplus?: number;
  lives_free_remaining?: number;
  recent_events?: Array<{ t: number; event: string; [k: string]: unknown }>;
  visible_threats?: Array<{ bearing_deg: number; distance: number; size: string }>;
  current_radio_channel?: string | null;
  [k: string]: unknown;
};

export type ChatRequest = {
  character: CharacterId;
  trigger: Trigger;
  pilot_utterance: string | null;
  game_state: GameState;
  history?: HistoryTurn[];
};

/**
 * A structured action Flere (or any acting character) asked the game
 * to perform. Register a handler per `name`; `input` is shaped by the
 * tool's schema on the Worker. Unknown names should be ignored —
 * the tool registry may grow faster than the game handlers do.
 */
export type Action = {
  name: string;
  input: Record<string, unknown>;
};

export type ChatResponse = {
  character: CharacterId;
  text: string;
  actions: Action[];
  usage?: { input_tokens: number; output_tokens: number };
  error?: boolean;
};

// Brief §Error handling: 8s client-side, 6s Worker-side.
const CLIENT_TIMEOUT_MS = 8_000;

/**
 * Call the copilot. Resolves with plain text + optional error flag.
 * Never throws on a bad LLM call — the Worker returns a fallback line
 * with `error: true`. This helper only throws on transport errors
 * (e.g. offline, timeout, unreachable Worker).
 */
export async function chat(
  req: ChatRequest,
  opts: { signal?: AbortSignal } = {},
): Promise<ChatResponse> {
  const ac = new AbortController();
  const timeoutId = setTimeout(() => ac.abort(), CLIENT_TIMEOUT_MS);
  opts.signal?.addEventListener("abort", () => ac.abort(), { once: true });

  try {
    const response = await fetch("/api/chat", {
      method: "POST",
      headers: { "content-type": "application/json" },
      signal: ac.signal,
      body: JSON.stringify(req),
    });

    if (!response.ok) {
      // Transport-level failure: return empty text + error flag so the
      // caller can skip rendering. The worker's own error path supplies
      // a per-character fallback line; we never hear it on transport
      // errors, and a hard-coded character voice would break register.
      return {
        character: req.character,
        text: "",
        actions: [],
        error: true,
      };
    }

    return (await response.json()) as ChatResponse;
  } catch {
    return {
      character: req.character,
      text: "",
      actions: [],
      error: true,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}
